// Template 3: Dark Neon
// Features: Dark background, neon glow effects, modern look

import { 
  Phone, 
  MessageCircle, 
  Mail, 
  Share2, 
  Download,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  Globe,
  MapPin,
  ExternalLink
} from 'lucide-react'

export function DarkNeonTemplate({ vcard, onSaveContact, onShare }) {
  return (
    <div className="dark-neon-template">
      <div className="dn-glow dn-glow-1"></div>
      <div className="dn-glow dn-glow-2"></div>
      
      <div className="dn-container">
        {/* Profile */}
        <div className="dn-profile">
          <div 
            className="dn-avatar"
            style={{ 
              backgroundImage: vcard.profileImage ? `url(${vcard.profileImage})` : 'none'
            }}
          >
            {!vcard.profileImage && (
              <span>{vcard.name?.[0] || 'U'}</span>
            )}
          </div>
          <h1 className="dn-name">{vcard.name || 'Your Name'}</h1>
          <p className="dn-title">{vcard.title || 'Your Title'}</p>
          {vcard.company && <p className="dn-company">{vcard.company}</p>}
        </div>

        {/* Action Buttons */}
        <div className="dn-actions">
          {vcard.phone && (
            <a href={`tel:${vcard.phone}`} className="dn-action-btn">
              <Phone size={20} />
              <span>Call</span>
            </a>
          )}
          {vcard.whatsapp && (
            <a 
              href={`https://wa.me/${vcard.whatsapp.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="dn-action-btn whatsapp"
            >
              <MessageCircle size={20} />
              <span>WhatsApp</span>
            </a>
          )}
          {vcard.email && (
            <a href={`mailto:${vcard.email}`} className="dn-action-btn">
              <Mail size={20} />
              <span>Email</span>
            </a>
          )}
        </div>

        {/* About */}
        {vcard.about && (
          <div className="dn-section">
            <h3 className="dn-section-title">About</h3>
            <p className="dn-text">{vcard.about}</p>
          </div>
        )}

        {/* Services */}
        {vcard.services && vcard.services.length > 0 && (
          <div className="dn-section">
            <h3 className="dn-section-title">Services</h3>
            <div className="dn-services">
              {vcard.services.map((service, i) => (
                <span key={i} className="dn-tag">{service}</span>
              ))}
            </div>
          </div>
        )}

        {/* Gallery */}
        {vcard.gallery && vcard.gallery.length > 0 && (
          <div className="dn-section">
            <h3 className="dn-section-title">Gallery</h3>
            <div className="dn-gallery">
              {vcard.gallery.map((image, i) => (
                <div key={i} className="dn-gallery-item">
                  <img src={image} alt={`Gallery ${i + 1}`} />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Social */}
        {vcard.social && Object.values(vcard.social).some(v => v) && (
          <div className="dn-section">
            <h3 className="dn-section-title">Connect</h3>
            <div className="dn-social-grid">
              {vcard.social.linkedin && (
                <a 
                  href={vcard.social.linkedin.startsWith('http') ? vcard.social.linkedin : `https://linkedin.com/in/${vcard.social.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dn-social-btn"
                >
                  <Linkedin size={22} />
                </a>
              )}
              {vcard.social.instagram && (
                <a 
                  href={vcard.social.instagram.startsWith('http') ? vcard.social.instagram : `https://instagram.com/${vcard.social.instagram.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dn-social-btn"
                >
                  <Instagram size={22} />
                </a>
              )}
              {vcard.social.facebook && (
                <a 
                  href={vcard.social.facebook.startsWith('http') ? vcard.social.facebook : `https://facebook.com/${vcard.social.facebook}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dn-social-btn"
                >
                  <Facebook size={22} />
                </a>
              )}
              {vcard.social.youtube && (
                <a 
                  href={vcard.social.youtube.startsWith('http') ? vcard.social.youtube : `https://youtube.com/${vcard.social.youtube}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dn-social-btn"
                >
                  <Youtube size={22} />
                </a>
              )}
              {vcard.social.twitter && (
                <a 
                  href={vcard.social.twitter.startsWith('http') ? vcard.social.twitter : `https://twitter.com/${vcard.social.twitter.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dn-social-btn"
                >
                  <Twitter size={22} />
                </a>
              )}
              {vcard.social.website && (
                <a 
                  href={vcard.social.website.startsWith('http') ? vcard.social.website : `https://${vcard.social.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dn-social-btn"
                >
                  <Globe size={22} />
                </a>
              )}
            </div>
          </div>
        )}

        {/* Location */}
        {vcard.address && (
          <a 
            href={`https://maps.google.com/?q=${encodeURIComponent(vcard.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="dn-location"
          >
            <MapPin size={18} />
            <span>{vcard.address}</span>
            <ExternalLink size={14} />
          </a>
        )}

        {/* Main Buttons */}
        <div className="dn-main-actions">
          <button onClick={onSaveContact} className="dn-btn primary">
            <Download size={18} />
            Save Contact
          </button>
          <button onClick={onShare} className="dn-btn secondary">
            <Share2 size={18} />
            Share
          </button>
        </div>

        {/* Footer */}
        <footer className="dn-footer">
          <p>Powered by <a href="/">Digital vCard</a></p>
        </footer>
      </div>
    </div>
  )
}

export default DarkNeonTemplate
